import type { BulkUpload } from "@/src/types/bulkUploadsModel"

import { toast } from "sonner"
import { create } from "zustand"
import { immer } from "zustand/middleware/immer"

import type { BaseStoreState } from "../types"

// Store state interface
export interface MagicSourceStoreState extends BaseStoreState {
    bulkUploadResult: BulkUpload | undefined
}

// Store actions interface
export interface MagicSourceStoreActions {
    pollStatus: (bulkJobId: string) => void
    setBulkResult: (result: BulkUpload | undefined) => void
}

export type MagicSourceStore = MagicSourceStoreState & MagicSourceStoreActions

export const useMagicSourceStore = create<MagicSourceStore>()(
    immer((set, get) => ({
        // State
        bulkUploadResult: undefined,
        loading: false,
        error: null,

        // Actions
        setBulkResult: (result: BulkUpload | undefined) => {
            set((state) => {
                state.bulkUploadResult = result
                state.error = null
            })

            // Handle toast notifications
            if (result) {
                if (result.status === "COMPLETED") {
                    toast.success(`${result.success} candidate${result.success === 1 ? "" : "s"} sourced successfully`)
                } else if (result.status === "FAILED") {
                    let title: string
                    let errorMessage: string

                    if (result.comments) {
                        const message = result.comments.substring(
                            result.comments.indexOf("{"),
                            result.comments.lastIndexOf("}") + 1
                        )
                        try {
                            errorMessage = JSON.parse(message).statusMessage
                        } catch (error) {
                            errorMessage = "Magic sourcing failed"
                        }
                    } else {
                        errorMessage = "Magic sourcing failed"
                    }

                    if (result.success)
                        title = `${result.success} candidate${result.success === 1 ? "" : "s"} sourced successfully`
                    else title = "Magic sourcing failed"

                    toast.error(title, { description: errorMessage })
                }
            }
        },

        pollStatus: (bulkJobId: string) => {
            if (!bulkJobId) return

            set((state) => {
                state.loading = true
                state.error = null
            })

            // This is a temporary implementation
            // The actual implementation should use the useBulkUploadAction hook
            // For now, we'll just set loading to false
            set((state) => {
                state.loading = false
            })
        },
    }))
)
